"use client"

import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { RefreshCw } from "lucide-react"

const MAX_AUTO_RETRIES = 4

// Module-level so the count survives component remounts (each failed retry
// unmounts + remounts this component with fresh state, so useState alone
// would reset the counter and loop forever).
let autoRetryCount = 0

// A failed JS chunk download can't be recovered by a soft router.refresh() —
// the client still points at a file that isn't there. The usual cause is a
// deploy renaming chunks while a tab was already open; a mobile network drop
// does it too. A single full page reload fetches the fresh asset manifest and
// fixes both. We only do this for download-shaped errors, not render bugs.
function isChunkLoadError(error?: Error & { name?: string; message?: string }) {
  const name = error?.name || ""
  const message = error?.message || ""
  return (
    name === "ChunkLoadError" ||
    /loading( css)? chunk [\d]+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /failed to fetch/i.test(message)
  )
}

export default function MainError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const router = useRouter()

  // Initialise from the module-level counter so we pick up where we left off
  // after a failed retry remounts this component.
  const [attempt, setAttempt] = useState(autoRetryCount)
  const gaveUp = attempt >= MAX_AUTO_RETRIES

  useEffect(() => {
    console.error("[main error boundary]", error)
  }, [error])

  // Chunk/download failures: hard-reload once instead of soft-retrying, which
  // can't recover a missing asset. Time-guarded so a genuinely broken build or
  // an offline device can't get stuck in a reload loop — if we already reloaded
  // in the last 20 s, we fall through to the normal soft-retry UI below.
  const chunkError = isChunkLoadError(error)
  useEffect(() => {
    if (!chunkError || typeof window === "undefined") return
    try {
      const KEY = "fp_chunk_reload_at"
      const last = Number(window.sessionStorage.getItem(KEY) || "0")
      if (Date.now() - last > 20000) {
        window.sessionStorage.setItem(KEY, String(Date.now()))
        window.location.reload()
      }
    } catch {
      // sessionStorage unavailable (private mode / blocked) — let the soft
      // retry below handle it rather than risk an unguarded reload loop.
    }
  }, [chunkError])

  // Auto-retry with gentle backoff: 4 s, 5 s, 6 s, 7 s
  useEffect(() => {
    if (gaveUp) return
    const delay = 4000 + attempt * 1000
    const timer = setTimeout(() => {
      autoRetryCount++
      setAttempt(autoRetryCount)
      router.refresh()
      reset()
    }, delay)
    return () => clearTimeout(timer)
  }, [attempt, gaveUp]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualReset = () => {
    autoRetryCount = 0
    setAttempt(0)
    router.refresh()
    reset()
  }

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
      <h1 className="text-2xl font-bold text-slate-900 mb-2">
        Something went wrong loading this page
      </h1>

      {!gaveUp ? (
        <>
          <p className="text-slate-500 text-sm mb-6 max-w-md">
            Our servers may be restarting after an update. Hang tight — we&apos;re
            retrying automatically.
          </p>
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-500 font-semibold rounded-lg text-sm">
            <RefreshCw size={15} className="animate-spin" />
            Retrying… ({attempt + 1} of {MAX_AUTO_RETRIES})
          </div>
        </>
      ) : (
        <>
          <p className="text-slate-500 text-sm mb-6 max-w-md">
            This is taking longer than usual. Your cart and account are safe.
          </p>
          <button
            onClick={handleManualReset}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#005555] hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors"
          >
            <RefreshCw size={16} />
            Try again
          </button>
        </>
      )}

      <p className="text-xs text-slate-400 mt-6">
        Still stuck? Email{" "}
        <a href="mailto:support@fastpeps.com" className="underline">
          support@fastpeps.com
        </a>{" "}
        or use the live chat.
      </p>
    </div>
  )
}
